import {
  Button,
  Checkbox,
  Combobox,
  Input,
  Loader,
  Pill,
  PillsInput,
  useCombobox
} from "@marketplace/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type ProductFormIndustrySelectProps = {
  data: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  placeholder?: string;
  error?: string;
  isLoading?: boolean;
};

export const ProductFormIndustrySelect: React.FC<
  ProductFormIndustrySelectProps
> = ({ data, value, onChange, label, placeholder, error, isLoading }) => {
  const { t } = useTranslation("common");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const [selectedValues, setSelectedValues] = useState<string[]>(value || []);

  // Sync with external value changes
  useEffect(() => {
    const currentValue = value || [];
    const hasChanged =
      selectedValues.length !== currentValue.length ||
      !selectedValues.every((val, idx) => val === currentValue[idx]);

    if (hasChanged) {
      setSelectedValues(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleValueSelect = (val: string) => {
    const newValues = selectedValues.includes(val)
      ? selectedValues.filter((v) => v !== val)
      : [...selectedValues, val];
    setSelectedValues(newValues);
  };

  const handleChoose = () => {
    onChange(selectedValues);
    combobox.closeDropdown();
  };

  const handleCancel = () => {
    setSelectedValues(value || []);
    combobox.closeDropdown();
  };

  return (
    <Input.Wrapper label={label} withAsterisk>
      <Combobox
        store={combobox}
        onOptionSubmit={handleValueSelect}
        classNames={{
          dropdown: "p-0 min-w-[300px]"
        }}
      >
        <Combobox.Target>
          <PillsInput
            pointer
            onClick={() => combobox.toggleDropdown()}
            rightSection={
              isLoading ? <Loader size="xs" /> : <Combobox.Chevron />
            }
            rightSectionPointerEvents="none"
            size="md"
            error={error}
          >
            <Pill.Group>
              {selectedValues.length === 0 ? (
                <PillsInput.Field
                  placeholder={placeholder || "Select target industries"}
                  readOnly
                  pointer
                />
              ) : (
                <>
                  {selectedValues.map((item) => (
                    <Pill
                      key={item}
                      withRemoveButton
                      onRemove={() => {
                        const newValues = selectedValues.filter(
                          (v) => v !== item
                        );
                        setSelectedValues(newValues);
                        onChange(newValues);
                      }}
                    >
                      {data.find((d) => d.value === item)?.label || item}
                    </Pill>
                  ))}
                  <PillsInput.Field readOnly pointer />
                </>
              )}
            </Pill.Group>
          </PillsInput>
        </Combobox.Target>

        <Combobox.Dropdown
          classNames={{
            dropdown: "rounded-lg"
          }}
        >
          <Combobox.Options>
            <div>
              {data.map((item) => (
                <Checkbox
                  key={item.value}
                  label={item.label}
                  checked={selectedValues.includes(item.value)}
                  onChange={() => handleValueSelect(item.value)}
                  classNames={{
                    root: "p-4",
                    label: "text-sm cursor-pointer",
                    labelWrapper: "w-full"
                  }}
                />
              ))}
            </div>
            <hr className="h-px border-0 bg-mischka-100" />
            <div className="flex justify-end gap-4 p-4">
              <Button
                size="sm"
                variant="secondary"
                className="w-[128px]"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                className="w-[128px]"
                onClick={handleChoose}
              >
                {t("confirm")}
              </Button>
            </div>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Input.Wrapper>
  );
};
